import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { QuillLogo } from "../components/QuillLogo";
import { Seo } from "../components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------------------------------------------------------------------------
// EDIT HERE. These are the swappable placeholders.
// ---------------------------------------------------------------------------
const PRE_ORDER = true;                       // flip to false on launch (1 Dec 2026)
const RELEASE_DATE = "1 December 2026";
const COVER_IMAGE_URL = "https://customer-assets-jai6qajn.emergentagent.net/job_quill-study/artifacts/f83b17e7cbab8b01_image.png"; // final 3D cover render
const AUTHOR_IMAGE_URL = "https://customer-assets-jai6qajn.emergentagent.net/job_quill-study/artifacts/52467a42b81a5087_image.png";  // Zukiswa portrait
const PRESS_EMAIL = "info@theravensquill.co.za";

// External retailers: fill in each href when the listing goes live.
const RETAILERS = [
  { id: "amazon", label: "Amazon", href: "#" },
  { id: "takealot", label: "Takealot", href: "#" },
  { id: "apple-books", label: "Apple Books", href: "#" },
  { id: "google-play", label: "Google Play Books", href: "#" },
  { id: "kobo", label: "Kobo", href: "#" },
];

const INK = "#1C1A17";
const PAPER = "#F4F1EA";
const PAPER_DEEP = "#ECE7DC";
const OXBLOOD = "#7A2E2E";
const OXBLOOD_WARM = "#B0573F";
const PEWTER = "#8A8F8A";
const STONE = "#A8A296";
const SLATE = "#6E6A5E";

const body = { fontFamily: "Lato, system-ui, sans-serif" };
const bodyLight = { fontFamily: "Lato, system-ui, sans-serif", fontWeight: 300 };

// Small-cap section eyebrow with the brand hairline rule.
const Eyebrow = ({ children, dark = false }) => (
  <div className="flex flex-col items-start">
    <span
      className="text-[11px] uppercase font-serif"
      style={{ letterSpacing: "0.25em", color: dark ? OXBLOOD_WARM : OXBLOOD }}
    >
      {children}
    </span>
    <span className="mt-4 block" style={{ width: 64, height: "1px", backgroundColor: dark ? OXBLOOD_WARM : OXBLOOD }} />
  </div>
);

const CoverPlaceholder = () => (
  <div
    className="relative w-full overflow-hidden"
    style={{ aspectRatio: "2 / 3", backgroundColor: INK, border: `1px solid ${PEWTER}` }}
    data-testid="book-cover-placeholder"
  >
    <div className="absolute inset-0 flex flex-col items-center justify-between py-10 px-8 text-center">
      <span className="text-[9px] uppercase" style={{ letterSpacing: "0.3em", color: PEWTER }}>
        The Raven&rsquo;s Quill
      </span>
      <div className="flex flex-col items-center">
        <QuillLogo height={64} variant="gold" />
        <h3 className="mt-7 font-serif leading-tight" style={{ color: PAPER, fontSize: "26px" }}>
          No One Is Coming To Save You
        </h3>
        <span className="mt-4 block" style={{ width: 44, height: "1px", backgroundColor: OXBLOOD_WARM }} />
        <span className="mt-4 text-[11px] uppercase" style={{ letterSpacing: "0.22em", color: PEWTER }}>
          Zukiswa Phoofolo
        </span>
      </div>
      <span className="text-[9px] uppercase" style={{ letterSpacing: "0.22em", color: OXBLOOD_WARM }}>
        Cover placeholder &middot; final cover to follow
      </span>
    </div>
  </div>
);

const AuthorMonogram = () => (
  <div
    className="flex items-center justify-center"
    style={{ aspectRatio: "1 / 1", backgroundColor: PAPER_DEEP, border: `1px solid ${STONE}` }}
    data-testid="author-monogram-placeholder"
  >
    <div className="flex flex-col items-center">
      <span className="font-serif italic" style={{ color: INK, fontSize: "56px", lineHeight: 1 }}>ZP</span>
      <span className="mt-4 block" style={{ width: 40, height: "1px", backgroundColor: OXBLOOD }} />
      <span className="mt-4 text-[10px] uppercase text-center" style={{ letterSpacing: "0.2em", color: SLATE }}>
        Portrait to follow
      </span>
    </div>
  </div>
);

const Book = () => {
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const [showSticky, setShowSticky] = useState(false);
  const [buyVisible, setBuyVisible] = useState(false);
  const [config, setConfig] = useState(null);
  const [checkoutMsg, setCheckoutMsg] = useState("");

  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [company, setCompany] = useState(""); // honeypot
  const [subState, setSubState] = useState("idle"); // idle | sending | done | error
  const sampleUrl = `${API}/book/sample`;

  const buyVerb = PRE_ORDER ? "Pre-order" : "Buy";

  useEffect(() => {
    document.title = "No One Is Coming To Save You · The Raven's Quill";
    fetch(`${API}/book/config`)
      .then((r) => r.json())
      .then(setConfig)
      .catch(() => setConfig(null));
  }, []);

  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => setShowSticky(!entry.isIntersecting),
      { threshold: 0, rootMargin: "-80px 0px 0px 0px" }
    );
    obs.observe(el);
    const buyEl = document.getElementById("buy");
    let buyObs;
    if (buyEl) {
      buyObs = new IntersectionObserver(
        ([entry]) => setBuyVisible(entry.isIntersecting),
        { threshold: 0.15 }
      );
      buyObs.observe(buyEl);
    }
    return () => {
      obs.disconnect();
      if (buyObs) buyObs.disconnect();
    };
  }, []);

  const scrollToBuy = () => {
    const el = document.getElementById("buy");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const buyDirect = () => {
    if (config && config.direct_checkout_available === false) {
      setCheckoutMsg("Direct ordering opens shortly. Please use a retailer below, or write to us.");
      return;
    }
    navigate("/no-one-is-coming/checkout");
  };

  const buyEbook = () => {
    if (config && config.direct_checkout_available === false) {
      setCheckoutMsg("The ebook shop opens shortly. Please use a retailer below, or write to us.");
      return;
    }
    navigate("/no-one-is-coming/ebook-checkout");
  };

  const subscribe = async (e) => {
    e.preventDefault();
    if (subState === "sending") return;
    setSubState("sending");
    try {
      const res = await fetch(`${API}/book/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, first_name: firstName, company, source: "free-sample" }),
      });
      if (!res.ok) throw new Error();
      setSubState("done");
      setEmail("");
      setFirstName("");
    } catch {
      setSubState("error");
    }
  };

  const priceLine =
    config && config.book_price
      ? `R${config.book_price}${config.delivery_fee_sa ? ` + delivery from R${config.delivery_fee_sa} (or free collection)` : ""}`
      : null;

  return (
    <div style={{ backgroundColor: PAPER, color: INK }} data-testid="book-landing-page">
      <Seo
        title="No One Is Coming To Save You | The Raven's Quill"
        ogTitle="No One Is Coming To Save You"
        description="Not motivation. Method. A field guide for anyone rebuilding. Pre-order the signed paperback, or get the ebook instantly."
        path="/no-one-is-coming"
        type="book"
        image="https://theravensquill.co.za/share-cover.jpg?v=1"
      />
      {/* Sticky buy button */}
      {showSticky && !buyVisible && (
        <button
          onClick={scrollToBuy}
          data-testid="sticky-buy-button"
          className="fixed bottom-6 right-6 z-50 px-6 py-3 text-[12px] uppercase font-serif transition-transform hover:-translate-y-0.5"
          style={{ letterSpacing: "0.2em", backgroundColor: OXBLOOD, color: PAPER, boxShadow: "0 2px 14px rgba(28,26,23,0.25)" }}
        >
          {buyVerb} the book
        </button>
      )}

      {/* HERO */}
      <section ref={heroRef} style={{ backgroundColor: "#120C08", color: PAPER }} data-testid="book-hero">
        <div className="mx-auto max-w-[1080px] px-6 md:px-10 py-20 md:py-28 grid md:grid-cols-[minmax(0,420px)_1fr] gap-10 md:gap-14 items-center">
          <div className="mx-auto w-full max-w-[420px]">
            {COVER_IMAGE_URL ? (
              <img
                src={COVER_IMAGE_URL}
                alt="No One Is Coming To Save You by Zukiswa Phoofolo, book cover"
                className="w-full block"
                style={{
                  WebkitMaskImage:
                    "linear-gradient(to right, transparent 0, #000 8%, #000 92%, transparent 100%), linear-gradient(to bottom, transparent 0, #000 6%, #000 94%, transparent 100%)",
                  WebkitMaskComposite: "source-in",
                  maskImage:
                    "linear-gradient(to right, transparent 0, #000 8%, #000 92%, transparent 100%), linear-gradient(to bottom, transparent 0, #000 6%, #000 94%, transparent 100%)",
                  maskComposite: "intersect",
                }}
              />
            ) : (
              <CoverPlaceholder />
            )}
          </div>
          <div>
            <h1 className="font-serif leading-[1.08]" style={{ fontSize: "clamp(34px, 6vw, 60px)", color: PAPER }}>
              No One Is Coming To Save You
            </h1>
            <p className="mt-6 text-[13px] uppercase font-serif" style={{ letterSpacing: "0.28em", color: OXBLOOD_WARM }}>
              Not motivation. Method.
            </p>
            <p className="mt-8 text-[16px]" style={{ ...bodyLight, color: "#D8D3C6", maxWidth: "46ch" }}>
              The commercial edition. Paperback and ebook.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-6">
              <button
                onClick={scrollToBuy}
                data-testid="hero-get-the-book"
                className="px-8 py-3.5 text-[12px] uppercase font-serif transition-transform hover:-translate-y-0.5"
                style={{ letterSpacing: "0.22em", backgroundColor: OXBLOOD_WARM, color: INK }}
              >
                {PRE_ORDER ? "Pre-order the book" : "Get the book"}
              </button>
              {PRE_ORDER && (
                <span className="text-[13px]" style={{ ...body, color: PEWTER }}>
                  Released {RELEASE_DATE}
                </span>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* THE HOOK */}
      <section className="mx-auto max-w-[760px] px-6 md:px-10 py-24 md:py-32 text-center" data-testid="book-hook">
        <p className="font-serif leading-[1.5]" style={{ fontSize: "clamp(22px, 3.4vw, 32px)", color: INK }}>
          You have read the other ones. They understood you for an afternoon and changed nothing by the weekend.
        </p>
        <p className="mt-8 font-serif italic" style={{ fontSize: "clamp(20px, 3vw, 28px)", color: OXBLOOD }}>
          This is not that book.
        </p>
      </section>

      {/* BUY */}
      <section id="buy" style={{ backgroundColor: PAPER_DEEP }} data-testid="book-buy">
        <div className="mx-auto max-w-[840px] px-6 md:px-10 py-20 md:py-24">
          <Eyebrow>Get the book</Eyebrow>
          <div className="mt-10">
            <button
              onClick={buyDirect}
              data-testid="buy-direct-button"
              className="w-full md:w-auto px-10 py-4 text-[13px] uppercase font-serif transition-transform hover:-translate-y-0.5"
              style={{ letterSpacing: "0.2em", backgroundColor: OXBLOOD, color: PAPER }}
            >
              {PRE_ORDER ? "Pre-order the signed paperback" : "Buy direct from The Raven's Quill"}
            </button>
            {PRE_ORDER && (
              <p className="mt-3 text-[14px]" style={{ ...body, color: INK }} data-testid="preorder-line">
                Pre-order now. Your signed copy ships from {RELEASE_DATE}.
              </p>
            )}
            {priceLine && (
              <p className="mt-2 text-[13px]" style={{ ...body, color: SLATE }}>
                Signed paperback &middot; {priceLine}
              </p>
            )}
            {PRE_ORDER && (
              <p className="mt-3 text-[12px]" style={{ ...body, color: SLATE, maxWidth: "58ch" }} data-testid="refund-line">
                Changed your mind before despatch? Email{" "}
                <a href="mailto:info@theravensquill.co.za?subject=Pre-order%20refund%3A%20No%20One%20Is%20Coming%20To%20Save%20You" style={{ color: OXBLOOD }}>info@theravensquill.co.za</a>{" "}
                for a full refund any time before your book ships.
              </p>
            )}
            {checkoutMsg && (
              <p className="mt-3 text-[14px] font-serif italic" style={{ color: OXBLOOD }} data-testid="checkout-message">
                {checkoutMsg}
              </p>
            )}
          </div>

          <div className="mt-6" data-testid="ebook-buy-block">
            <button
              onClick={buyEbook}
              data-testid="buy-ebook-button"
              className="w-full md:w-auto px-10 py-4 text-[13px] uppercase font-serif transition-transform hover:-translate-y-0.5"
              style={{ letterSpacing: "0.2em", backgroundColor: INK, color: PAPER }}
            >
              Buy the ebook (instant download)
            </button>
            <p className="mt-2 text-[13px]" style={{ ...body, color: SLATE }}>
              Ebook{config && config.ebook_price ? ` · R${config.ebook_price}` : ""} &middot; delivered instantly to your inbox
            </p>
          </div>

          <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 gap-4" data-testid="retailer-buttons">
            {RETAILERS.map((r) => {
              const pending = r.href === "#";
              return (
                <a
                  key={r.id}
                  href={r.href}
                  data-testid={`retailer-${r.id}`}
                  target={pending ? undefined : "_blank"}
                  rel="noopener noreferrer"
                  aria-disabled={pending}
                  title={pending ? "Link coming soon" : undefined}
                  onClick={(e) => { if (pending) e.preventDefault(); }}
                  className="flex items-center justify-center text-center px-5 py-3 text-[12px] uppercase font-serif transition-colors"
                  style={{ letterSpacing: "0.16em", border: `1px solid ${STONE}`, color: pending ? SLATE : INK, cursor: pending ? "default" : "pointer" }}
                  onMouseEnter={(e) => { if (!pending) { e.currentTarget.style.borderColor = OXBLOOD; e.currentTarget.style.color = OXBLOOD; } }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = STONE; e.currentTarget.style.color = pending ? SLATE : INK; }}
                >
                  {r.label}
                </a>
              );
            })}
          </div>

          <p className="mt-8 text-[14px]" style={{ ...body, color: SLATE }}>
            Pre-order is the signed paperback. Ebook available instantly. Also on Amazon, Takealot, Apple, Google and Kobo.
          </p>
        </div>
      </section>

      {/* THE BOOK */}
      <section className="mx-auto max-w-[760px] px-6 md:px-10 py-20 md:py-24" data-testid="book-description">
        <Eyebrow>The book</Eyebrow>
        <div className="mt-10 space-y-6 text-[17px] leading-[1.85]" style={{ ...body, color: INK, maxWidth: "65ch" }}>
          <p>
            No One Is Coming To Save You is a field guide for anyone rebuilding, in one part of life or in all of it. It begins where the self-help shelf ends, with the hard truth that no ruling, no rescue, no person, and no sum of money is coming to save you, and with the stranger truth hidden inside it: that this is the beginning of your freedom, not the end of your hope.
          </p>
          <p>
            It does not ask you to think positively or wait to be delivered. It hands you the method by which a self is actually made, drawn from anthropology, psychology, and one woman&rsquo;s own unmaking, and it teaches you to run that method on yourself, on purpose. To take yourself apart. To build, in the clear space that remains, the person you choose to be.
          </p>
          <p className="font-serif italic" style={{ fontFamily: undefined, color: OXBLOOD, fontSize: "19px" }}>
            No one is coming. You are the one who was coming, all along.
          </p>
        </div>
      </section>

      {/* FROM INSIDE THE BOOK */}
      <section style={{ backgroundColor: PAPER_DEEP }} data-testid="book-extract">
        <div className="mx-auto max-w-[760px] px-6 md:px-10 py-20 md:py-24">
          <Eyebrow>From inside the book</Eyebrow>
          <p className="mt-10 text-[17px] leading-[1.85]" style={{ ...body, color: INK, maxWidth: "65ch" }}>
            The book is not a pep talk. It is a protocol, run over three deliberate days and held in place by a daily practice afterward. A few of the moves it asks of you:
          </p>
          <ul className="mt-8 space-y-6" style={{ maxWidth: "65ch" }}>
            {[
              ["Read your own life like fieldwork.", "Spend fourteen days as a stranger to your own patterns, and write down what you find before it fades."],
              ["Name the beliefs that cost you.", "Code your field notes the way a researcher codes a life, until the few load-bearing beliefs underneath show themselves."],
              ["Dissolve, then rebuild.", "Take the old self apart on purpose, sit in the empty space, and install the one you choose, in your own hand, on your own authority."],
            ].map(([lead, rest], i) => (
              <li key={i} className="text-[17px] leading-[1.85]" style={{ ...body, color: INK }}>
                <span className="font-serif" style={{ color: OXBLOOD, fontWeight: 700 }}>{lead}</span>{" "}
                {rest}
              </li>
            ))}
          </ul>
          <p className="mt-10 font-serif italic text-[18px] leading-[1.7]" style={{ color: INK, maxWidth: "62ch" }}>
            The method is old. Every culture that ever deliberately made a person knew it. This book puts it back in your hands.
          </p>
        </div>
      </section>

      {/* THE AUTHOR */}
      <section className="mx-auto max-w-[900px] px-6 md:px-10 py-20 md:py-24" data-testid="book-author">
        <Eyebrow>The author</Eyebrow>
        <div className="mt-10 grid md:grid-cols-[220px_1fr] gap-10 md:gap-14 items-start">
          <div className="mx-auto w-full max-w-[220px]">
            {AUTHOR_IMAGE_URL ? (
              <img src={AUTHOR_IMAGE_URL} alt="Zukiswa Phoofolo" className="w-full" style={{ border: `1px solid ${STONE}` }} />
            ) : (
              <AuthorMonogram />
            )}
          </div>
          <div>
            <h2 className="font-serif italic" style={{ fontSize: "clamp(26px, 4vw, 34px)", color: INK }}>
              Zukiswa Phoofolo
            </h2>
            <div className="mt-6 space-y-5 text-[17px] leading-[1.85]" style={{ ...body, color: INK, maxWidth: "62ch" }}>
              <p>
                One question has run through all of Zukiswa Phoofolo&rsquo;s work: how a human being becomes who they are, through inheritance, childhood, culture, and history, and how much of that becoming, once it is made visible, a person can consciously author. She has pursued it across seventeen years in early childhood development throughout the Global South, in sub-Saharan Africa and across several countries of Asia, and now through The Raven&rsquo;s Quill, a fine press that studies a single life closely enough to reveal the architecture of its becoming.
              </p>
              <p>
                This book is what happened when she turned that question on her own life, and studied her own unmaking and remaking with the discipline she had spent her career giving to others. It is the practical half of a lifelong inquiry: once the architecture is visible, what can a person deliberately reconstruct. It is her first book.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* EMAIL CAPTURE */}
      <section style={{ backgroundColor: PAPER_DEEP }} data-testid="book-signup">
        <div className="mx-auto max-w-[640px] px-6 md:px-10 py-20 md:py-24 text-center">
          <h2 className="font-serif" style={{ fontSize: "clamp(24px, 3.6vw, 32px)", color: INK }}>
            Read the first chapter free
          </h2>
          <p className="mt-5 text-[16px] leading-[1.8]" style={{ ...body, color: SLATE, maxWidth: "52ch", marginInline: "auto" }}>
            Leave your email and I will send you the opening chapter, and a note when the book goes live.
          </p>
          {subState === "done" ? (
            <div className="mt-10" data-testid="signup-success">
              <p className="font-serif italic text-[19px]" style={{ color: OXBLOOD }}>
                Check your inbox, your chapter is on its way. If it is not there in a few minutes, check spam, or download it here.
              </p>
              <a
                href={sampleUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="signup-download-button"
                className="mt-7 inline-block px-8 py-3.5 text-[12px] uppercase font-serif transition-transform hover:-translate-y-0.5"
                style={{ letterSpacing: "0.2em", backgroundColor: OXBLOOD, color: PAPER }}
              >
                Download your sample chapter
              </a>
            </div>
          ) : (
            <>
            <form onSubmit={subscribe} className="mt-10 flex flex-col gap-4 items-stretch max-w-[460px] mx-auto" data-testid="signup-form">
              <label htmlFor="book-first-name" className="sr-only">First name (optional)</label>
              <input
                id="book-first-name"
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="First name (optional)"
                data-testid="signup-firstname-input"
                className="bg-transparent py-3 px-1 text-[16px] outline-none rounded-none"
                style={{ ...body, borderBottom: `1px solid ${STONE}`, color: INK }}
              />
              <label htmlFor="book-email" className="sr-only">Your email address</label>
              <input
                id="book-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email address"
                data-testid="signup-email-input"
                className="bg-transparent py-3 px-1 text-[16px] outline-none rounded-none"
                style={{ ...body, borderBottom: `1px solid ${STONE}`, color: INK }}
              />
              {/* Honeypot */}
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
                aria-hidden="true"
              />
              <button
                type="submit"
                disabled={subState === "sending"}
                data-testid="signup-submit"
                className="px-8 py-3.5 text-[12px] uppercase font-serif transition-transform hover:-translate-y-0.5 disabled:opacity-60 whitespace-nowrap"
                style={{ letterSpacing: "0.2em", backgroundColor: OXBLOOD, color: PAPER }}
              >
                {subState === "sending" ? "Sending" : "Send me the chapter"}
              </button>
            </form>
            <p className="mt-4 text-[12px]" style={{ ...body, color: SLATE }}>
              No spam. Just the chapter and the odd note from The Raven&rsquo;s Quill.
            </p>
            <p className="mt-2 text-[11px]" style={{ ...body, color: SLATE, maxWidth: "48ch", marginInline: "auto" }}>
              By signing up you agree to receive the chapter and occasional updates from The Raven&rsquo;s Quill. You can unsubscribe at any time.
            </p>
            </>
          )}
          {subState === "error" && (
            <p className="mt-5 text-[14px] font-serif italic" style={{ color: OXBLOOD }} data-testid="signup-error">
              Something went wrong. Please try again.
            </p>
          )}
        </div>
      </section>

      {/* FOOTER */}
      <footer style={{ backgroundColor: INK, color: PAPER }} data-testid="book-footer">
        <div className="mx-auto max-w-[760px] px-6 md:px-10 py-16 text-center">
          <div className="flex justify-center">
            <QuillLogo height={72} variant="gold" />
          </div>
          <p className="mt-6 font-serif italic" style={{ fontSize: "22px", color: PAPER }}>
            The Raven&rsquo;s Quill
          </p>
          <p className="mt-4 text-[11px] uppercase font-serif" style={{ letterSpacing: "0.28em", color: OXBLOOD_WARM }}>
            Where transformation gets written
          </p>
          <hr className="border-0 mx-auto mt-8" style={{ width: 64, height: "1px", backgroundColor: PEWTER }} />
          <div className="mt-8 flex flex-col items-center gap-4">
            <a
              href="/"
              data-testid="book-footer-site-link"
              className="text-[11px] uppercase font-serif transition-colors"
              style={{ letterSpacing: "0.24em", color: "#D8D3C6" }}
              onMouseEnter={(e) => (e.currentTarget.style.color = OXBLOOD_WARM)}
              onMouseLeave={(e) => (e.currentTarget.style.color = "#D8D3C6")}
            >
              More from The Raven&rsquo;s Quill
            </a>
            <a
              href={`mailto:${PRESS_EMAIL}?subject=${encodeURIComponent("Press enquiry: No One Is Coming To Save You")}`}
              data-testid="book-footer-press-link"
              className="text-[11px] uppercase font-serif transition-colors"
              style={{ letterSpacing: "0.24em", color: "#D8D3C6" }}
              onMouseEnter={(e) => (e.currentTarget.style.color = OXBLOOD_WARM)}
              onMouseLeave={(e) => (e.currentTarget.style.color = "#D8D3C6")}
            >
              Media and press enquiries
            </a>
          </div>
          <p className="mt-10 text-[12px]" style={{ ...bodyLight, color: PEWTER }}>
            &copy; 2026 The Raven&rsquo;s Quill. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Book;
